import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CloseIcon from "@mui/icons-material/Close";
import { alpha, Box, Button, Dialog, DialogContent, DialogTitle, Fade, IconButton, Typography, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";

const TermsDialog = ({ open, onClose, content, highlightSection, showAgreeButton = false, onAgree }) => {
  const contentRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    if (open && highlightSection && contentRef.current) {
      const sectionMap = {
        masterAgreement: "1. Services Offered",
        termsOfUse: "2. Eligibility",
        privacyPolicy: "4. Commitment Fees"
      };

      const targetText = sectionMap[highlightSection];
      if (targetText) {
        const textContent = contentRef.current.textContent;
        const startIndex = textContent.indexOf(targetText);
        if (startIndex !== -1) {
          const range = document.createRange();
          const textNodes = [];
          contentRef.current.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              textNodes.push(node);
            }
          });

          let currentPos = 0;
          for (let i = 0; i < textNodes.length; i++) {
            const node = textNodes[i];
            if (currentPos + node.length > startIndex) {
              range.setStart(node, startIndex - currentPos);
              break;
            }
            currentPos += node.length;
          }

          range.collapse(true);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);

          range.startContainer.parentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    }
  }, [open, highlightSection]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      TransitionComponent={Fade}
      transitionDuration={400}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: "16px",
          maxHeight: "90vh",
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          p: 4,
          pb: 3,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          backgroundColor: theme.palette.background.paper,
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2
            }}
          >
            <ArticleOutlinedIcon 
              sx={{ 
                color: theme.palette.primary.main,
                fontSize: '24px'
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                letterSpacing: '-0.01em',
                mb: 0.5
              }}
            >
              Terms and Conditions
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                lineHeight: 1.5
              }}
            >
              Please read these terms carefully before proceeding
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            position: 'absolute',
            right: 24,
            top: 24,
            backgroundColor: alpha(theme.palette.divider, 0.04),
            backdropFilter: 'blur(4px)',
            '&:hover': {
              backgroundColor: alpha(theme.palette.divider, 0.08),
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          p: 0,
          '&::-webkit-scrollbar': {
            width: '6px',
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.divider, 0.15),
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: alpha(theme.palette.divider, 0.25)
            }
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <Box 
          ref={contentRef}
          sx={{ 
            p: 4,
            pb: showAgreeButton ? 2 : 4,
            '& h1, & h2, & h3': {
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 2.5,
              letterSpacing: '-0.02em'
            }
          }}
        >
          <Typography
            component="div"
            sx={{
              color: alpha(theme.palette.text.primary, 0.85),
              fontSize: '0.9375rem',
              lineHeight: 1.75,
              letterSpacing: '0.01em',
              '& p': {
                mb: 2.5,
                position: 'relative',
                '&:hover': {
                  color: theme.palette.text.primary,
                  transition: 'color 0.2s ease-in-out'
                }
              },
              '& ul, & ol': {
                mt: -1,
                mb: 2.5,
                pl: 3
              },
              '& li': {
                mb: 1.5,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.primary.main, 0.5)
                }
              },
              '& strong': {
                color: theme.palette.text.primary,
                fontWeight: 500
              },
              '& section': {
                mb: 4,
                pb: 4,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                '&:last-child': {
                  mb: 0,
                  pb: 0,
                  borderBottom: 'none'
                }
              }
            }}
          >
            {content.split('\n\n').map((paragraph, index) => (
              <Typography 
                key={index} 
                component="p"
                sx={{
                  mb: 2.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:last-child': {
                    mb: 0
                  }
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Typography>
        </Box>
        {showAgreeButton && (
          <Box
            sx={{
              p: 3,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              backdropFilter: 'blur(8px)'
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onAgree?.();
                onClose();
              }}
              sx={{
                px: 4,
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9375rem',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              I Agree
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TermsDialog; 